import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CreditCard, Sparkles, RefreshCw, AlertTriangle, CheckCircle2, HelpCircle, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_BADGE = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  'bestätigt': { label: 'Bestätigt', cls: 'bg-sky-100 text-sky-700 border-sky-200', Icon: ShieldCheck },
  warnung: { label: 'Warnung', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: AlertTriangle },
  kritisch: { label: 'Kritisch', cls: 'bg-red-100 text-red-700 border-red-200', Icon: AlertTriangle },
  unbekannt: { label: 'Unbekannt', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', Icon: HelpCircle },
};

export default function CardsOverviewPage() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [pendingLinks, setPendingLinks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [deletedCards, setDeletedCards] = useState(null); // null=nie geprüft, []=nichts gefunden

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payrexx/cards/overview`);
      setCards(res.data.cards || []);
      setPendingLinks(res.data.pending_links || 0);
    } catch (e) {
      toast.error('Karten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runAiCheck = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/payrexx/cards/ai-check`);
      const s = res.data.summary || {};
      toast.success(`KI-Prüfung: ${res.data.checked} Karten geprüft — ${s.ok || 0} OK, ${s.warnung || 0} Warnungen, ${s.kritisch || 0} kritisch`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KI-Prüfung fehlgeschlagen');
    } finally {
      setChecking(false);
    }
  };

  const runDuplicateScan = async () => {
    try {
      const res = await axios.post(`${API}/payrexx/cards/duplicate-scan`);
      if (res.data.duplicates_found > 0) {
        toast.warning(`${res.data.duplicates_found} Duplikat(e) gefunden — WhatsApp/E-Mail-Warnung gesendet (antworte mit 1 oder 2)`);
      } else {
        toast.success(`${res.data.scanned} Karten gescannt — keine Duplikate gefunden`);
      }
    } catch {
      toast.error('Duplikat-Scan fehlgeschlagen');
    }
  };

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/payrexx/cards/reconcile`);
      const acts = res.data.activated || [];
      if (acts.length > 0) {
        toast.success(`${acts.length} Karte(n) nachgetragen: ${acts.map((a) => a.name).join(', ')}`);
      } else {
        toast.info(`${res.data.checked || 0} offene Link(s) geprüft — keine neue Karte gefunden`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Prüfung fehlgeschlagen');
    } finally {
      setReconciling(false);
    }
  };

  // 08/2026: Findet Karten, die direkt bei Payrexx gelöscht/ungültig wurden.
  const runVerifyDeleted = async (mark = false) => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/payrexx/cards/verify-deleted?mark=${mark}`);
      const found = res.data.deleted || [];
      setDeletedCards(found);
      if (mark) {
        toast.success(`${found.length} bei Payrexx gelöschte Karte(n) im CRM entfernt`);
        setDeletedCards(null);
        await load();
      } else if (found.length > 0) {
        toast.warning(`${found.length} Karte(n) bei Payrexx gelöscht/ungültig — siehe Liste`);
      } else {
        const extra = res.data.no_transaction_id
          ? ` (${res.data.no_transaction_id} ohne Transaktions-ID nicht prüfbar)` : '';
        toast.success(`Alle ${res.data.checked} geprüften Karten sind bei Payrexx noch gültig${extra}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payrexx-Prüfung fehlgeschlagen');
    } finally {
      setVerifying(false);
    }
  };

  const confirmCard = async (e, c, revoke = false) => {    e.stopPropagation();
    try {
      if (revoke) {
        await axios.delete(`${API}/payrexx/cards/${c.token_id}/confirm`);
        toast.success('Bestätigung aufgehoben — Karte wird wieder KI-geprüft');
      } else {
        await axios.post(`${API}/payrexx/cards/${c.token_id}/confirm`);
        toast.success(`Zuordnung für ${c.customer_name} als korrekt bestätigt`);
      }
      await load();
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const critical = cards.filter((c) => c.ai_check?.verdict === 'kritisch').length;
  const warnings = cards.filter((c) => c.ai_check?.verdict === 'warnung').length;

  return (
    <div className="p-6 space-y-6" data-testid="cards-overview-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6" /> Hinterlegte Karten
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {cards.length} aktive Karten · {pendingLinks} offene Karten-Links
            {critical > 0 && <span className="text-red-600 font-medium"> · {critical} kritische Zuordnung(en)!</span>}
            {warnings > 0 && <span className="text-amber-600"> · {warnings} Warnung(en)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="cards-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
          <Button variant="outline" onClick={runDuplicateScan} data-testid="cards-duplicate-scan-btn">
            <AlertTriangle className="w-4 h-4 mr-2" /> Duplikate scannen
          </Button>
          <Button variant="outline" onClick={runReconcile} disabled={reconciling} data-testid="cards-reconcile-btn">
            <CreditCard className={`w-4 h-4 mr-2 ${reconciling ? 'animate-pulse' : ''}`} />
            {reconciling ? 'Prüfe bei Payrexx…' : 'Offene Links jetzt prüfen'}
          </Button>
          <Button variant="outline" onClick={() => runVerifyDeleted(false)} disabled={verifying || cards.length === 0} data-testid="cards-verify-deleted-btn">
            <AlertTriangle className={`w-4 h-4 mr-2 ${verifying ? 'animate-pulse' : ''}`} />
            {verifying ? 'Prüfe bei Payrexx…' : 'Bei Payrexx gelöschte finden'}
          </Button>
          <Button onClick={runAiCheck} disabled={checking || cards.length === 0} data-testid="cards-ai-check-btn">
            <Sparkles className={`w-4 h-4 mr-2 ${checking ? 'animate-pulse' : ''}`} />
            {checking ? 'KI prüft…' : 'KI-Prüfung starten'}
          </Button>
        </div>
      </div>

      {deletedCards && deletedCards.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="deleted-cards-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {deletedCards.length} Karte(n) bei Payrexx gelöscht/ungültig
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Diese Karten sind im CRM noch aktiv, existieren aber bei Payrexx nicht mehr. Entferne sie, damit keine
                Abbuchung mehr darauf versucht wird.
              </p>
            </div>
            <Button size="sm" onClick={() => runVerifyDeleted(true)} disabled={verifying}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="remove-deleted-cards-btn">
              Im CRM entfernen
            </Button>
          </div>
          <ul className="mt-3 space-y-1">
            {deletedCards.map((d) => (
              <li key={d.token_id} className="text-sm text-amber-900 flex items-center gap-2"
                  data-testid={`deleted-card-${d.token_id}`}>
                <CreditCard className="w-3.5 h-3.5" />
                <button className="font-medium underline underline-offset-2"
                        onClick={() => navigate(`/customers?id=${d.customer_id}`)}>
                  {d.customer_name}
                </button>
                <span className="text-amber-700">· {d.card}</span>
                {d.customer_email && <span className="text-amber-600 text-xs">· {d.customer_email}</span>}
                <span className="text-xs text-amber-500">({d.payrexx_state})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cards-table">
              <thead>
                <tr className="border-b bg-zinc-50 text-left text-zinc-500">
                  <th className="px-4 py-3 font-medium">Kunde</th>
                  <th className="px-4 py-3 font-medium">Karteninhaber</th>
                  <th className="px-4 py-3 font-medium">Karte</th>
                  <th className="px-4 py-3 font-medium">Ablauf</th>
                  <th className="px-4 py-3 font-medium">Quelle</th>
                  <th className="px-4 py-3 font-medium">KI-Prüfung</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">Lade Karten…</td></tr>
                ) : cards.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">Keine aktiven Karten hinterlegt</td></tr>
                ) : cards.map((c) => {
                  const v = VERDICT_BADGE[c.ai_check?.verdict];
                  return (
                    <tr key={c.token_id} className="border-b last:border-0 hover:bg-zinc-50 cursor-pointer"
                        onClick={() => navigate(`/customers?id=${c.customer_id}`)}
                        data-testid={`card-row-${c.token_id}`}>
                      <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                      <td className="px-4 py-3">{c.cardholder_name || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-4 py-3 capitalize">{c.card_brand} ****{c.card_last_four}</td>
                      <td className="px-4 py-3">{c.expiry || '—'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500">
                        {c.activated_via === 'nightly_reconcile' ? 'Nachtprüfung'
                          : c.activated_via === 'self_heal' ? 'Self-Heal'
                          : c.activated_via === 'webhook_email_match' ? 'E-Mail-Match' : 'Webhook'}
                      </td>
                      <td className="px-4 py-3">
                        {v ? (
                          <Badge variant="outline" className={v.cls} title={c.ai_check?.reason}>
                            <v.Icon className="w-3 h-3 mr-1" /> {v.label}
                          </Badge>
                        ) : <span className="text-zinc-400 text-xs">Nicht geprüft</span>}
                        {c.ai_check?.reason && !['ok', 'bestätigt'].includes(c.ai_check?.verdict) && (
                          <div className="text-xs text-zinc-500 mt-1">{c.ai_check.reason}</div>
                        )}
                        {['warnung', 'kritisch', 'unbekannt'].includes(c.ai_check?.verdict) && !c.admin_confirmed && (
                          <Button size="sm" variant="outline" className="mt-1 h-7 text-xs"
                                  onClick={(e) => confirmCard(e, c)}
                                  data-testid={`confirm-card-${c.token_id}`}>
                            <ShieldCheck className="w-3 h-3 mr-1" /> Als korrekt bestätigen
                          </Button>
                        )}
                        {c.admin_confirmed && (
                          <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs text-zinc-400"
                                  onClick={(e) => confirmCard(e, c, true)}
                                  data-testid={`revoke-card-${c.token_id}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> Aufheben
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
